import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { SplashScreen } from '../components/SplashScreen';
import { TelegramLoginButton } from '../components/TelegramLoginButton';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import {
  isTelegramWebApp,
  loadTelegramWebAppScript,
  looksLikeTelegramEnvironment,
  resolveTelegramInitData,
} from '../utils/telegramWebApp';

export function LoginPage() {
  const { user, config, loading, refresh, authError, loginWithPassword } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showTelegram, setShowTelegram] = useState(() => looksLikeTelegramEnvironment());
  const inTelegram = isTelegramWebApp();
  const displayError = error ?? authError;

  if (loading) {
    return <SplashScreen label={inTelegram ? 'Вход через Telegram…' : 'Загрузка'} />;
  }
  if (user) return <Navigate to="/" replace />;

  const onPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await loginWithPassword(username, password);
    setBusy(false);
    if (!res.ok) setError(res.message ?? 'Не удалось войти');
  };

  return (
    <div style={{ maxWidth: 420, margin: '3rem auto' }}>
      <div className="card" style={{ textAlign: 'center' }}>
        <h1 style={{ marginTop: 0 }}>Твой провайдер</h1>
        <p className="muted">
          {inTelegram
            ? 'Не удалось войти автоматически.'
            : 'Войдите логином и паролем или через Telegram'}
        </p>

        {!inTelegram && (
          <form onSubmit={onPasswordLogin} style={{ marginTop: '1.25rem', textAlign: 'left' }}>
            <label className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
              Логин
            </label>
            <input
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              style={{ width: '100%', marginTop: '0.25rem' }}
            />
            <label className="muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
              Пароль
            </label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              minLength={8}
              style={{ width: '100%', marginTop: '0.25rem' }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              {busy ? 'Вход…' : 'Войти'}
            </button>
          </form>
        )}

        {displayError && inTelegram && (
          <p className="error-text" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            {displayError}
          </p>
        )}

        {inTelegram && (
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: '1.25rem' }}
            onClick={async () => {
              setError(null);
              await loadTelegramWebAppScript();
              const initData = await resolveTelegramInitData();
              if (!initData) {
                setError('Telegram не передал данные для входа. Закройте и откройте приложение снова.');
                return;
              }
              const res = await api.loginTelegramWebApp(initData);
              if (res.data?.authenticated) {
                await refresh();
                return;
              }
              setError(res.error?.message ?? 'Не удалось войти через Telegram');
            }}
          >
            Повторить вход
          </button>
        )}

        {!inTelegram && (
          <div style={{ marginTop: '1.5rem' }}>
            {!showTelegram ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowTelegram(true)}
              >
                Войти через Telegram
              </button>
            ) : (
              <>
                <p className="muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  Telegram создаст аккаунт и выдаст логин/пароль для сайта
                </p>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <TelegramLoginButton
                    botUsername={config?.bot_username ?? ''}
                    onError={setError}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {!inTelegram && displayError && (
          <p className="error-text" style={{ marginTop: '1rem' }}>
            {displayError}
          </p>
        )}
        {!inTelegram && (
          <p className="muted" style={{ marginTop: '1.5rem', fontSize: '0.8rem' }}>
            Нет аккаунта? Войдите через Telegram один раз — логин и пароль появятся в настройках.
          </p>
        )}
      </div>
    </div>
  );
}
